import { useEffect, useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CubeIcon,
  FolderIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Card, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { servicesAdminApi, ServiceCategory, Service } from '@/api/services'
import clsx from 'clsx'

type TabType = 'categories' | 'services'

export default function AdminServicesPage() {
  const [activeTab, setActiveTab] = useState<TabType>('categories')
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)

  // Category Modal
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null)
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: 'cube',
    order: 0,
    is_active: true,
  })
  const [savingCategory, setSavingCategory] = useState(false)

  // Service Modal
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [editingService, setEditingService] = useState<Service | null>(null)
  const [serviceForm, setServiceForm] = useState({
    name: '',
    category: 0,
    short_description: '',
    description: '',
    icon: 'cube',
    is_free: true,
    is_active: true,
    order: 0,
    external_url: '',
    provider: '',
  })
  const [savingService, setSavingService] = useState(false)

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteType, setDeleteType] = useState<'category' | 'service'>('category')
  const [itemToDelete, setItemToDelete] = useState<ServiceCategory | Service | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [cats, svcs] = await Promise.all([
        servicesAdminApi.getServiceCategories(),
        servicesAdminApi.getServices(),
      ])
      setCategories(cats)
      setServices(svcs)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Category handlers
  const handleOpenCreateCategory = () => {
    setEditingCategory(null)
    setCategoryForm({
      name: '',
      description: '',
      icon: 'cube',
      order: categories.length + 1,
      is_active: true,
    })
    setShowCategoryModal(true)
  }

  const handleOpenEditCategory = (category: ServiceCategory) => {
    setEditingCategory(category)
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      icon: category.icon || 'cube',
      order: category.order,
      is_active: category.is_active,
    })
    setShowCategoryModal(true)
  }

  const handleSaveCategory = async () => {
    setSavingCategory(true)
    try {
      if (editingCategory) {
        await servicesAdminApi.updateServiceCategory(editingCategory.id, categoryForm)
      } else {
        await servicesAdminApi.createServiceCategory(categoryForm)
      }
      setShowCategoryModal(false)
      fetchData()
    } catch (error) {
      console.error('Error saving category:', error)
    } finally {
      setSavingCategory(false)
    }
  }

  // Service handlers
  const handleOpenCreateService = () => {
    setEditingService(null)
    setServiceForm({
      name: '',
      category: categories[0]?.id || 0,
      short_description: '',
      description: '',
      icon: 'cube',
      is_free: true,
      is_active: true,
      order: services.length + 1,
      external_url: '',
      provider: '',
    })
    setShowServiceModal(true)
  }

  const handleOpenEditService = (service: Service) => {
    setEditingService(service)
    const categoryId = typeof service.category === 'number'
      ? service.category
      : (service.category as ServiceCategory).id
    setServiceForm({
      name: service.name,
      category: categoryId,
      short_description: service.short_description || '',
      description: service.description || '',
      icon: service.icon || 'cube',
      is_free: service.is_free,
      is_active: service.is_active,
      order: service.order,
      external_url: service.external_url || '',
      provider: service.provider || '',
    })
    setShowServiceModal(true)
  }

  const handleSaveService = async () => {
    setSavingService(true)
    try {
      if (editingService) {
        await servicesAdminApi.updateService(editingService.id, serviceForm)
      } else {
        await servicesAdminApi.createService(serviceForm)
      }
      setShowServiceModal(false)
      fetchData()
    } catch (error) {
      console.error('Error saving service:', error)
    } finally {
      setSavingService(false)
    }
  }

  // Delete handlers
  const handleOpenDelete = (type: 'category' | 'service', item: ServiceCategory | Service) => {
    setDeleteType(type)
    setItemToDelete(item)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!itemToDelete) return
    setDeleting(true)
    try {
      if (deleteType === 'category') {
        await servicesAdminApi.deleteServiceCategory(itemToDelete.id)
      } else {
        await servicesAdminApi.deleteService(itemToDelete.id)
      }
      setShowDeleteModal(false)
      setItemToDelete(null)
      fetchData()
    } catch (error) {
      console.error('Error deleting:', error)
    } finally {
      setDeleting(false)
    }
  }

  const getCategoryName = (service: Service): string => {
    if (typeof service.category === 'number') {
      const cat = categories.find(c => c.id === service.category)
      return cat?.name || 'N/A'
    }
    return (service.category as ServiceCategory).name
  }

  if (loading) {
    return <InlineSpinner message="Caricamento servizi..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Servizi</h1>
          <p className="text-gray-600">Gestisci categorie e servizi della Compliance Clinic</p>
        </div>
        <Button onClick={activeTab === 'categories' ? handleOpenCreateCategory : handleOpenCreateService}>
          <PlusIcon className="h-5 w-5 mr-2" />
          {activeTab === 'categories' ? 'Nuova Categoria' : 'Nuovo Servizio'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('categories')}
            className={clsx(
              'py-4 px-1 border-b-2 font-medium text-sm',
              activeTab === 'categories'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            <FolderIcon className="h-5 w-5 inline mr-2" />
            Categorie ({categories.length})
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={clsx(
              'py-4 px-1 border-b-2 font-medium text-sm',
              activeTab === 'services'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            <CubeIcon className="h-5 w-5 inline mr-2" />
            Servizi ({services.length})
          </button>
        </nav>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        categories.length > 0 ? (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordine</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servizi</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attiva</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-gray-500">{category.order}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <FolderIcon className="h-5 w-5 text-primary-600" />
                          <span className="font-medium text-gray-900">{category.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {category.description || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">{category.services_count || 0}</td>
                      <td className="px-4 py-4">
                        {category.is_active ? (
                          <CheckIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500" />
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEditCategory(category)}
                            className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                            title="Modifica"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleOpenDelete('category', category)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Card>
            <EmptyState
              icon={<FolderIcon className="h-12 w-12" />}
              title="Nessuna categoria"
              description="Crea la prima categoria per i servizi"
              action={{
                label: 'Nuova Categoria',
                onClick: handleOpenCreateCategory
              }}
            />
          </Card>
        )
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        services.length > 0 ? (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordine</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attivo</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {services.map((service) => (
                    <tr key={service.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-gray-500">{service.order}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <CubeIcon className="h-5 w-5 text-primary-600" />
                          <div>
                            <span className="font-medium text-gray-900 block">{service.name}</span>
                            <span className="text-xs text-gray-500">{service.short_description}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">{getCategoryName(service)}</td>
                      <td className="px-4 py-4">
                        <span className={clsx(
                          'px-2 py-1 text-xs font-medium rounded-full',
                          service.is_free
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        )}>
                          {service.is_free ? 'Gratuito' : 'A Pagamento'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">{service.provider || '-'}</td>
                      <td className="px-4 py-4">
                        {service.is_active ? (
                          <CheckIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <XMarkIcon className="h-5 w-5 text-red-500" />
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenEditService(service)}
                            className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                            title="Modifica"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleOpenDelete('service', service)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            title="Elimina"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : (
          <Card>
            <EmptyState
              icon={<CubeIcon className="h-12 w-12" />}
              title="Nessun servizio"
              description={categories.length === 0
                ? "Crea prima una categoria, poi potrai aggiungere servizi"
                : "Crea il primo servizio"
              }
              action={categories.length > 0 ? {
                label: 'Nuovo Servizio',
                onClick: handleOpenCreateService
              } : undefined}
            />
          </Card>
        )
      )}

      {/* Category Modal */}
      <Modal
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        title={editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Nome categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
              <textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Descrizione categoria"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
                <input
                  type="number"
                  value={categoryForm.order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min={0}
                />
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={categoryForm.is_active}
                    onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Categoria attiva</span>
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowCategoryModal(false)}>Annulla</Button>
          <Button onClick={handleSaveCategory} disabled={savingCategory || !categoryForm.name}>
            {savingCategory ? 'Salvataggio...' : 'Salva'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Service Modal */}
      <Modal
        isOpen={showServiceModal}
        onClose={() => setShowServiceModal(false)}
        title={editingService ? 'Modifica Servizio' : 'Nuovo Servizio'}
        size="lg"
      >
        <ModalBody>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Nome servizio"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                <select
                  value={serviceForm.category}
                  onChange={(e) => setServiceForm({ ...serviceForm, category: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value={0}>Seleziona categoria</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione breve</label>
              <input
                type="text"
                value={serviceForm.short_description}
                onChange={(e) => setServiceForm({ ...serviceForm, short_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Breve descrizione del servizio"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione completa</label>
              <textarea
                value={serviceForm.description}
                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={4}
                placeholder="Descrizione dettagliata del servizio"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                <input
                  type="text"
                  value={serviceForm.provider}
                  onChange={(e) => setServiceForm({ ...serviceForm, provider: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Fornitore del servizio"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL esterno</label>
                <input
                  type="url"
                  value={serviceForm.external_url}
                  onChange={(e) => setServiceForm({ ...serviceForm, external_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
                <input
                  type="number"
                  value={serviceForm.order}
                  onChange={(e) => setServiceForm({ ...serviceForm, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min={0}
                />
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={serviceForm.is_free}
                    onChange={(e) => setServiceForm({ ...serviceForm, is_free: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Gratuito</span>
                </label>
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={serviceForm.is_active}
                    onChange={(e) => setServiceForm({ ...serviceForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Attivo</span>
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowServiceModal(false)}>Annulla</Button>
          <Button
            onClick={handleSaveService}
            disabled={savingService || !serviceForm.name || !serviceForm.category}
          >
            {savingService ? 'Salvataggio...' : 'Salva'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={`Elimina ${deleteType === 'category' ? 'Categoria' : 'Servizio'}`}
      >
        <ModalBody>
          {itemToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare {deleteType === 'category' ? 'la categoria' : 'il servizio'}{' '}
                <span className="font-semibold">{itemToDelete.name}</span>?
              </p>
              {deleteType === 'category' && (itemToDelete as ServiceCategory).services_count > 0 && (
                <p className="text-sm text-amber-600">
                  Attenzione: questa categoria contiene {(itemToDelete as ServiceCategory).services_count} servizi.
                </p>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>Annulla</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
